import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSportContent } from '../hooks/useSportContent';
import { useMembership } from '../hooks/useMembership';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { MembershipUpgrade } from '../components/MembershipUpgrade';
import PremiumPrompt from '../components/PremiumPrompt';
import { getRecoveryUrl } from '../config/recoveryLinks';
import '../styles/content.css';

export const VideoAnalysisScreen: React.FC = () => {
  const { user } = useAuth();
  const { theme, currentSport } = useTheme();
  const { content, loading, error } = useSportContent(currentSport.id);
  const { membership, canAnalyzeVideo, getRemainingVideos, loadMembership } = useMembership(user?.id || '');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  const [videoInputType, setVideoInputType] = useState<'file' | 'youtube' | 'upload'>('upload');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');

  // Debug logging (remove in production)
  console.log('VideoAnalysisScreen - currentSport:', currentSport);
  console.log('VideoAnalysisScreen - content:', content);
  console.log('VideoAnalysisScreen - loading:', loading);
  console.log('VideoAnalysisScreen - error:', error);

  if (loading) {
    return (
      <div className="screen-container">
        <div className="loading">Loading video analysis content...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="screen-container">
        <div className="error">
          <h2>Error Loading Video Analysis</h2>
          <p>There was an error loading video analysis content: {error}</p>
          <p>Current sport: {currentSport.name}</p>
        </div>
      </div>
    );
  }

  if (!content?.videoAnalysis) {
    return (
      <div className="screen-container">
        <div className="no-content">
          <h2>Video Analysis</h2>
          <p>Video analysis content is not available for this sport.</p>
          <p>Current sport: {currentSport.name}</p>
          <p>Content loaded: {content ? 'Yes' : 'No'}</p>
        </div>
      </div>
    );
  }

  const videoAnalysis = content.videoAnalysis;
  const analysisCheck = canAnalyzeVideo();
  
  // Temporary: Allow video analysis for testing (remove in production)
  const canAnalyzeForTesting = true;

  const handleVideoUpload = () => {
    if (!user) {
      setShowPremiumPrompt(true);
      return;
    }
    
    if (!analysisCheck.canAnalyze) {
      setShowUpgrade(true);
      return;
    }

    if (videoInputType === 'file') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'video/*';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const videoId = file.name.replace(/\.[^/.]+$/, '') || `file-${Date.now()}`;
        const sport = currentSport.name.toLowerCase();
        setIsAnalyzing(true);
        setShowAnalysis(false);
        try {
          const apiUrl = import.meta.env.VITE_VIDEO_ANALYSIS_API_URL;
          const supportedSports = ['tennis', 'pickleball', 'golf', 'padel', 'badminton', 'hiking', 'racquetball', 'squash', 'running', 'cycling', 'swimming', 'strength'];
          if (apiUrl && supportedSports.includes(sport)) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('sport', sport);
            const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/video/analyze`, {
              method: 'POST',
              body: formData,
            });
            if (res.ok) {
              const result = await res.json();
              setAnalysisResult({ ...result, videoId });
              setShowAnalysis(true);
              setIsAnalyzing(false);
              return;
            }
          }
        } catch (err) {
          console.warn('Backend analysis failed, using mock:', err);
        }
        setTimeout(() => {
          setAnalysisResult(generateMockAnalysis(videoId, sport));
          setShowAnalysis(true);
          setIsAnalyzing(false);
        }, 3000);
      };
      input.click();
    }
  };

  const handleYouTubeUpload = () => {
    if (!user) {
      setShowPremiumPrompt(true);
      return;
    }
    if (!analysisCheck.canAnalyze) {
      setShowUpgrade(true);
      return;
    }
    // Create a file input element for video upload to YouTube
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadToYouTubeShorts(file);
      }
    };
    input.click();
  };

  const uploadToYouTubeShorts = async (file: File) => {
    const maxSize = 100 * 1024 * 1024; // 100MB limit
    if (file.size > maxSize) {
      alert('File too large! Please select a video under 100MB.');
      return;
    }

    const videoId = file.name.replace(/\.[^/.]+$/, '') || `video-${Date.now()}`;
    const sport = currentSport.name.toLowerCase();
    setIsUploading(true);
    setIsAnalyzing(true);
    setShowAnalysis(false);

    try {
      const apiUrl = import.meta.env.VITE_VIDEO_ANALYSIS_API_URL;
      const supportedSports = ['tennis', 'pickleball', 'golf', 'padel', 'badminton', 'hiking', 'racquetball', 'squash', 'running', 'cycling', 'swimming', 'strength'];
      if (apiUrl && supportedSports.includes(sport)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sport', sport);
        const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/video/analyze`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const result = await res.json();
          setAnalysisResult({ ...result, videoId });
          setShowAnalysis(true);
          setIsUploading(false);
          setIsAnalyzing(false);
          return;
        }
      }
    } catch (err) {
      console.warn('Backend analysis failed, using mock:', err);
    }
    setTimeout(() => {
      setAnalysisResult(generateMockAnalysis(videoId, sport));
      setShowAnalysis(true);
      setIsUploading(false);
      setIsAnalyzing(false);
    }, 3000);
  };

  const generateMockAnalysis = (videoId: string, sport: string) => {
    const sportSpecificAnalysis = {
      golf: {
        swingAnalysis: {
          backswing: { score: 85, feedback: "Good shoulder turn, maintain spine angle" },
          downswing: { score: 78, feedback: "Slight early release, work on lag" },
          followThrough: { score: 92, feedback: "Excellent finish position" }
        },
        keyMetrics: {
          swingPlane: "On plane",
          tempo: "1.2:1 (Good)",
          clubPath: "Slightly in-to-out",
          faceAngle: "Square at impact"
        },
        recommendations: [
          "Focus on maintaining lag in the downswing",
          "Practice with alignment sticks for swing plane",
          "Work on tempo with metronome drills"
        ]
      },
      pickleball: {
        swingAnalysis: {
          serve: { score: 88, feedback: "Good power, work on placement" },
          groundstroke: { score: 82, feedback: "Solid contact, improve footwork" },
          volley: { score: 75, feedback: "Good positioning, work on soft hands" }
        },
        keyMetrics: {
          paddleAngle: "Optimal",
          contactPoint: "Slightly late",
          footwork: "Needs improvement",
          courtPosition: "Good"
        },
        recommendations: [
          "Practice serve placement drills",
          "Focus on early preparation",
          "Work on split-step timing"
        ]
      },
      tennis: {
        swingAnalysis: {
          serve: { score: 90, feedback: "Excellent technique and power" },
          forehand: { score: 85, feedback: "Good topspin, work on consistency" },
          backhand: { score: 78, feedback: "Solid two-hander, improve timing" }
        },
        keyMetrics: {
          racquetHead: "Good speed",
          contactPoint: "Optimal",
          footwork: "Excellent",
          courtPosition: "Strategic"
        },
        recommendations: [
          "Maintain current serve technique",
          "Practice forehand consistency drills",
          "Work on backhand timing with wall drills"
        ]
      },
      hiking: {
        swingAnalysis: {
          technique: { score: 88, feedback: "Good posture and stride length" },
          pace: { score: 82, feedback: "Consistent rhythm, work on efficiency" },
          safety: { score: 95, feedback: "Excellent safety practices and awareness" }
        },
        keyMetrics: {
          strideLength: "Optimal",
          packWeight: "Well distributed",
          pace: "Consistent",
          safety: "Excellent"
        },
        recommendations: [
          "Continue practicing proper uphill technique",
          "Work on downhill braking and control",
          "Maintain excellent safety practices"
        ]
      },
      padel: {
        swingAnalysis: {
          serve: { score: 86, feedback: "Good underhand serve technique" },
          forehand: { score: 85, feedback: "Solid bandeja foundation" },
          backhand: { score: 83, feedback: "Good backhand wall control" },
          volley: { score: 82, feedback: "Good net positioning" }
        },
        keyMetrics: {
          paddleAngle: "Optimal",
          contactPoint: "Good",
          footwork: "Good",
          courtPosition: "Strategic"
        },
        recommendations: [
          "Practice wall shots",
          "Work on net play",
          "Focus on lob defense"
        ]
      },
      badminton: {
        swingAnalysis: {
          serve: { score: 86, feedback: "Good serve technique" },
          clear: { score: 88, feedback: "Good overhead clear extension" },
          smash: { score: 87, feedback: "Good smash power and timing" },
          drop: { score: 84, feedback: "Good drop shot control" }
        },
        keyMetrics: {
          racquetHead: "Good speed",
          contactPoint: "Optimal",
          footwork: "Good",
          courtPosition: "Strategic"
        },
        recommendations: [
          "Practice footwork",
          "Work on net play",
          "Focus on shot variety"
        ]
      },
      racquetball: {
        swingAnalysis: {
          serve: { score: 85, feedback: "Good drive serve technique" },
          forehand: { score: 84, feedback: "Solid kill shot setup" },
          backhand: { score: 82, feedback: "Good wall control" },
          ceiling: { score: 80, feedback: "Work on ceiling ball consistency" }
        },
        keyMetrics: {
          racquetHead: "Good speed",
          contactPoint: "Good",
          footwork: "Good",
          courtPosition: "Strategic"
        },
        recommendations: [
          "Practice kill shot accuracy",
          "Work on ceiling ball placement",
          "Focus on court positioning"
        ]
      },
      squash: {
        swingAnalysis: {
          drive: { score: 86, feedback: "Good length and width" },
          volley: { score: 84, feedback: "Solid volley technique" },
          drop: { score: 82, feedback: "Good drop shot control" },
          boast: { score: 80, feedback: "Work on boast accuracy" }
        },
        keyMetrics: {
          racquetHead: "Good speed",
          contactPoint: "Good",
          footwork: "Good",
          courtPosition: "Strategic"
        },
        recommendations: [
          "Practice drive consistency",
          "Work on volley timing",
          "Focus on T-position recovery"
        ]
      },
      running: {
        swingAnalysis: {
          form: { score: 85, feedback: "Good cadence, work on foot strike" },
          posture: { score: 82, feedback: "Slight forward lean, relax shoulders" },
          efficiency: { score: 80, feedback: "Reduce vertical oscillation" }
        },
        keyMetrics: {
          cadence: "172 spm",
          strideLength: "Optimal",
          footStrike: "Midfoot",
          posture: "Good"
        },
        recommendations: [
          "Focus on 170-180 cadence",
          "Land under center of gravity",
          "Relax upper body"
        ]
      },
      cycling: {
        swingAnalysis: {
          pedaling: { score: 86, feedback: "Good cadence, smooth circles" },
          position: { score: 84, feedback: "Relax shoulders, stable core" },
          efficiency: { score: 82, feedback: "Consistent power output" }
        },
        keyMetrics: {
          cadence: "88 rpm",
          power: "Consistent",
          position: "Good",
          efficiency: "Optimal"
        },
        recommendations: [
          "Maintain 80-100 rpm cadence",
          "Core strengthening for stability",
          "Check bike fit if discomfort"
        ]
      },
      swimming: {
        swingAnalysis: {
          stroke: { score: 84, feedback: "Good catch, work on rotation" },
          breathing: { score: 86, feedback: "Bilateral breathing solid" },
          bodyPosition: { score: 82, feedback: "Hips at surface, reduce drag" }
        },
        keyMetrics: {
          strokeCount: "18/25m",
          catch: "Good",
          rotation: "Adequate",
          bodyPosition: "Streamlined"
        },
        recommendations: [
          "Early vertical forearm in catch",
          "Rotate from hips for breathing",
          "Include rotator cuff prehab"
        ]
      },
      strength: {
        swingAnalysis: {
          form: { score: 88, feedback: "Good bracing, neutral spine" },
          movement: { score: 85, feedback: "Solid pattern, control descent" },
          lockout: { score: 86, feedback: "Full range, good finish" }
        },
        keyMetrics: {
          bracing: "Solid",
          barPath: "Efficient",
          depth: "Adequate",
          control: "Good"
        },
        recommendations: [
          "Maintain core brace throughout",
          "Progressive overload gradually",
          "Include deload weeks"
        ]
      }
    };

    return {
      videoId,
      sport,
      timestamp: new Date().toISOString(),
      overallScore: Math.floor(Math.random() * 20) + 80, // 80-100
      analysis: sportSpecificAnalysis[sport as keyof typeof sportSpecificAnalysis] || sportSpecificAnalysis.golf,
      poseEstimation: {
        keyPoints: 17,
        confidence: 0.94,
        trackingQuality: "Excellent"
      },
      improvements: [
        "Continue current practice routine",
        "Focus on identified weak points",
        "Record progress videos weekly"
      ]
    };
  };

  const handleYouTubeAnalysis = async () => {
    if (!youtubeUrl.trim()) {
      alert('Please enter a YouTube URL');
      return;
    }

    const videoId = extractYouTubeVideoId(youtubeUrl);
    if (!videoId) {
      alert(`Please enter a valid YouTube URL.\n\nSupported formats:\n• https://www.youtube.com/watch?v=VIDEO_ID\n• https://youtu.be/VIDEO_ID\n• https://youtube.com/embed/VIDEO_ID\n• https://youtube.com/shorts/VIDEO_ID\n\nExample: https://www.youtube.com/watch?v=dQw4w9WgXcQ`);
      return;
    }

    const sport = currentSport.name.toLowerCase();
    setIsAnalyzing(true);
    setShowAnalysis(false);

    try {
      const apiUrl = import.meta.env.VITE_VIDEO_ANALYSIS_API_URL;
      const supportedSports = ['tennis', 'pickleball', 'golf', 'padel', 'badminton', 'hiking', 'racquetball', 'squash', 'running', 'cycling', 'swimming', 'strength'];
      if (apiUrl && supportedSports.includes(sport)) {
        const formData = new FormData();
        formData.append('url', youtubeUrl.trim());
        formData.append('sport', sport);
        const res = await fetch(`${apiUrl.replace(/\/$/, '')}/api/video/analyze-url`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const result = await res.json();
          setAnalysisResult({ ...result, videoId });
          setShowAnalysis(true);
          setIsAnalyzing(false);
          return;
        }
      }
    } catch (err) {
      console.warn('Backend analysis failed, using mock:', err);
    }
    setTimeout(() => {
      setAnalysisResult(generateMockAnalysis(videoId, sport));
      setShowAnalysis(true);
      setIsAnalyzing(false);
    }, 3000);
  };

  const extractYouTubeVideoId = (url: string): string | null => {
    // Enhanced regex to handle more YouTube URL formats including Shorts
    const patterns = [
      // Standard watch URLs
      /(?:youtube\.com\/watch\?v=)([^"&?\/\s]{11})/,
      // Short URLs
      /(?:youtu\.be\/)([^"&?\/\s]{11})/,
      // Embed URLs
      /(?:youtube\.com\/embed\/)([^"&?\/\s]{11})/,
      // Mobile URLs
      /(?:m\.youtube\.com\/watch\?v=)([^"&?\/\s]{11})/,
      // Playlist URLs with video ID
      /(?:youtube\.com\/watch\?.*v=)([^"&?\/\s]{11})/,
      // Live URLs
      /(?:youtube\.com\/live\/)([^"&?\/\s]{11})/,
      // YouTube Shorts URLs
      /(?:youtube\.com\/shorts\/)([^"&?\/\s]{11})/,
      // Mobile Shorts URLs
      /(?:m\.youtube\.com\/shorts\/)([^"&?\/\s]{11})/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return null;
  };

  return (
    <div className="screen-container">
      <div className="video-analysis-screen">
        <div className="video-analysis-header">
          <h1 style={{ color: theme.colors.primary }}>{videoAnalysis.title}</h1>
          <p className="video-analysis-description">{videoAnalysis.description}</p>
          <p style={{ color: theme.colors.success, fontWeight: 'bold' }}>
            ✅ Video Analysis is now available for {currentSport.name}!
          </p>
        </div>

        {/* How to Use + Recovery and Rehab - when no analysis shown yet */}
        {!showAnalysis && (
          <>
            <div className="help-text" style={{ marginBottom: '1rem', padding: '1rem', background: theme.colors.surface, borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
              <h3>How to Use Video Analysis</h3>
              <ol>
                <li><strong>Upload or paste a URL:</strong> Choose a video file, YouTube URL, or upload to YouTube Shorts</li>
                <li><strong>Get AI analysis:</strong> Receive pose estimation, technique breakdown, and key metrics</li>
                <li><strong>Review recommendations:</strong> Apply personalized tips and track your progress</li>
              </ol>
            </div>
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0f8f0', borderRadius: '8px', border: '1px solid #4caf5040' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🏥 Recovery and Rehab</h3>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9em', color: '#555' }}>
                Experiencing pain or want injury prevention tips? Get your personalized recovery plan.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <Link
                  to="/game-analysis"
                  style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    background: '#4caf50',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.9em'
                  }}
                >
                  Shot Analysis & Injuries →
                </Link>
                <a
                  href={getRecoveryUrl(currentSport.name, 'injury prevention', 'Injuries Overview')}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    color: '#2e7d32',
                    border: '2px solid #2e7d32',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.9em'
                  }}
                >
                  MendOnBend Recovery →
                </a>
              </div>
            </div>
          </>
        )}

        {/* Premium Feature Gate */}
        {!canAnalyzeForTesting && !analysisCheck.canAnalyze && (
          <div className="premium-gate">
            <div className="premium-gate-content">
              <h3>🎥 Premium Video Analysis</h3>
              <p>Get professional video analysis with pose estimation and personalized recommendations.</p>
              <div className="premium-benefits">
                <div className="benefit-item">
                  <span className="benefit-icon">🎯</span>
                  <span>Pose estimation and movement analysis</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">📊</span>
                  <span>Swing technique breakdown</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">👟</span>
                  <span>Footwork and positioning analysis</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">💡</span>
                  <span>Personalized improvement recommendations</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">📈</span>
                  <span>Progress tracking over time</span>
                </div>
              </div>
              <button 
                className="upgrade-button"
                onClick={() => setShowUpgrade(true)}
                style={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.background
                }}
              >
                Upgrade to Premium
              </button>
            </div>
          </div>
        )}

                       {/* Video Upload Section */}
               {(canAnalyzeForTesting || analysisCheck.canAnalyze) && (
                 <div className="video-upload-section">
                   <div className="upload-card">
                     <h3>📹 Analyze Your Video</h3>
                     <p>Get professional analysis of your technique</p>
                     
                     {/* YouTube Shorts Benefits */}
                     <div style={{ 
                       backgroundColor: theme.colors.surface, 
                       padding: '15px', 
                       borderRadius: '8px', 
                       marginBottom: '20px',
                       border: `1px solid ${theme.colors.primary}`,
                       borderLeft: `4px solid ${theme.colors.primary}`
                     }}>
                       <h4 style={{ color: theme.colors.primary, margin: '0 0 10px 0' }}>🎬 Why YouTube Shorts Are Perfect for Analysis</h4>
                       <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '0.9em' }}>
                         <li><strong>Upload & Analyze:</strong> Upload directly to YouTube Shorts and get instant analysis</li>
                         <li><strong>Auto-Hashtagging:</strong> Videos automatically tagged with #MySportsCoach and sport-specific hashtags</li>
                         <li><strong>No Storage Costs:</strong> Videos stay on YouTube, no expensive cloud storage</li>
                         <li><strong>Community Building:</strong> Connect with other players through branded hashtags</li>
                         <li><strong>Optimal Length:</strong> 60 seconds or less keeps analysis focused</li>
                         <li><strong>Easy Sharing:</strong> Share your technique videos with coaches and friends</li>
                         <li><strong>Mobile Friendly:</strong> Record directly from your phone and analyze instantly</li>
                         <li><strong>Professional Quality:</strong> Same analysis quality as file uploads</li>
                       </ul>
                     </div>
              
                                   {/* Input Type Selector */}
                     <div className="input-type-selector">
                       <button
                         className={`input-type-btn ${videoInputType === 'upload' ? 'active' : ''}`}
                         onClick={() => setVideoInputType('upload')}
                         style={{
                           backgroundColor: videoInputType === 'upload' ? theme.colors.primary : theme.colors.surface,
                           color: videoInputType === 'upload' ? 'white' : theme.colors.text
                         }}
                       >
                         🎬 Upload to YouTube Shorts
                       </button>
                       <button
                         className={`input-type-btn ${videoInputType === 'youtube' ? 'active' : ''}`}
                         onClick={() => setVideoInputType('youtube')}
                         style={{
                           backgroundColor: videoInputType === 'youtube' ? theme.colors.primary : theme.colors.surface,
                           color: videoInputType === 'youtube' ? 'white' : theme.colors.text
                         }}
                       >
                         📺 YouTube URL
                       </button>
                       <button
                         className={`input-type-btn ${videoInputType === 'file' ? 'active' : ''}`}
                         onClick={() => setVideoInputType('file')}
                         style={{
                           backgroundColor: videoInputType === 'file' ? theme.colors.primary : theme.colors.surface,
                           color: videoInputType === 'file' ? 'white' : theme.colors.text
                         }}
                       >
                         📁 File Upload
                       </button>
                     </div>

              {videoInputType === 'upload' ? (
                <div className="upload-section">
                  <p style={{ color: theme.colors.success, fontSize: '0.9em', marginBottom: '10px' }}>
                    🎬 <strong>Upload to YouTube Shorts:</strong> Upload your video directly to YouTube and get instant analysis!
                  </p>
                  <p style={{ color: theme.colors.textSecondary, fontSize: '0.8em', marginBottom: '15px' }}>
                    💡 Perfect for technique videos - upload once, analyze forever!
                  </p>
                  
                  {isUploading ? (
                    <div className="upload-progress">
                      <div className="progress-bar" style={{ 
                        width: '100%', 
                        height: '20px', 
                        backgroundColor: theme.colors.surface,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: '10px'
                      }}>
                        <div style={{ 
                          width: `${uploadProgress}%`, 
                          height: '100%', 
                          backgroundColor: theme.colors.primary,
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <p style={{ textAlign: 'center', color: theme.colors.text }}>
                        Uploading to YouTube Shorts... {uploadProgress}%
                      </p>
                    </div>
                  ) : (
                    <button
                      className="upload-button"
                      onClick={handleYouTubeUpload}
                      style={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.background,
                        width: '100%',
                        padding: '15px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    >
                      🎬 Upload Video to YouTube Shorts
                    </button>
                  )}
                  
                  {uploadedVideoUrl && (
                    <div style={{ 
                      marginTop: '15px', 
                      padding: '15px', 
                      backgroundColor: theme.colors.surface,
                      borderRadius: '8px',
                      border: `1px solid ${theme.colors.primary}`
                    }}>
                      <p style={{ color: theme.colors.success, margin: '0 0 10px 0', fontWeight: 'bold' }}>
                        ✅ Video uploaded successfully to YouTube Shorts!
                      </p>
                      <p style={{ color: theme.colors.text, fontSize: '0.9em', margin: '0 0 10px 0' }}>
                        <strong>YouTube URL:</strong> <a href={uploadedVideoUrl} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary }}>
                          {uploadedVideoUrl}
                        </a>
                      </p>
                      <div style={{ 
                        backgroundColor: theme.colors.background,
                        padding: '10px',
                        borderRadius: '5px',
                        border: `1px solid ${theme.colors.border}`
                      }}>
                        <p style={{ color: theme.colors.text, fontSize: '0.8em', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                          🏷️ Auto-added hashtags:
                        </p>
                        <p style={{ color: theme.colors.primary, fontSize: '0.8em', margin: '0', fontFamily: 'monospace' }}>
                          #MySportsCoach #mysportscoach.online #{currentSport.name.toLowerCase()}Technique #{currentSport.name.toLowerCase()}Analysis #SportsAnalysis #TechniqueImprovement #VideoAnalysis
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : videoInputType === 'youtube' ? (
                <div className="youtube-input-section">
                  <p style={{ color: theme.colors.success, fontSize: '0.9em', marginBottom: '10px' }}>
                    💡 <strong>Cost-Effective:</strong> YouTube URLs are free to analyze and don't require file storage!
                  </p>
                  <p style={{ color: theme.colors.primary, fontSize: '0.9em', marginBottom: '10px', fontWeight: 'bold' }}>
                    🎬 <strong>Pro Tip:</strong> YouTube Shorts (60 seconds or less) are perfect for technique analysis!
                  </p>
                  <input
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=VIDEO_ID, https://youtu.be/VIDEO_ID, or https://youtube.com/shorts/VIDEO_ID"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '5px',
                      marginBottom: '10px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ color: theme.colors.textSecondary, fontSize: '0.8em', marginBottom: '10px' }}>
                    💡 Tip: Copy the URL directly from your browser's address bar when watching a YouTube video
                  </p>
                  <p style={{ color: theme.colors.textSecondary, fontSize: '0.8em', marginBottom: '10px' }}>
                    🎯 Best for analysis: Short clips (10-60 seconds) showing specific techniques or swings
                  </p>
                                    <button
                    className="analyze-button"
                    onClick={handleYouTubeAnalysis}
                    disabled={isAnalyzing}
                    style={{
                      backgroundColor: isAnalyzing ? theme.colors.textSecondary : theme.colors.primary,
                      color: theme.colors.background,
                      width: '100%',
                      cursor: isAnalyzing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isAnalyzing ? '🔄 Analyzing Video...' : '🎯 Analyze YouTube Video'}
                  </button>
                </div>
              ) : (
                <div className="file-upload-section">
                  <div className="remaining-videos">
                    <span className="remaining-count">{getRemainingVideos()}</span>
                    <span>videos remaining this month</span>
                  </div>
                  <button 
                    className="upload-button"
                    onClick={handleVideoUpload}
                    style={{
                      backgroundColor: theme.colors.primary,
                      color: theme.colors.background
                    }}
                  >
                    Choose Video File
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analysis Types */}
        <div className="analysis-types-section">
          <h2>Analysis Types Available</h2>
          <div className="analysis-types-grid">
            {videoAnalysis.analysisTypes.map((analysisType) => (
              <div key={analysisType.id} className="analysis-type-card">
                <h3>{analysisType.name}</h3>
                <p>{analysisType.description}</p>
                <div className="key-points">
                  <h4>Key Analysis Points:</h4>
                  <ul>
                    {analysisType.keyPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div className="common-issues">
                  <h4>Common Issues We Analyze:</h4>
                  <ul>
                    {analysisType.commonIssues.map((issue, index) => (
                      <li key={index}>{issue.issue}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="features-section">
          <h2>Video Analysis Features</h2>
          <div className="features-grid">
            {videoAnalysis.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <span className="feature-icon">✨</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Personalized Recommendations */}
        <div className="recommendations-section">
          <h2>Personalized Recommendations</h2>
          <div className="recommendations-grid">
            {Object.entries(videoAnalysis.personalizedRecommendations).map(([level, recommendations]) => (
              <div key={level} className="recommendation-card">
                <h3 className={`level-${level}`}>{level.charAt(0).toUpperCase() + level.slice(1)}</h3>
                <ul>
                  {recommendations.map((recommendation, index) => (
                    <li key={index}>{recommendation}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Tracking */}
        <div className="progress-tracking-section">
          <h2>Progress Tracking</h2>
          <div className="progress-metrics">
            <div className="metrics-grid">
              {videoAnalysis.progressTracking.metrics.map((metric, index) => (
                <div key={index} className="metric-card">
                  <span className="metric-icon">📊</span>
                  <span>{metric}</span>
                </div>
              ))}
            </div>
            <div className="improvements-list">
              <h3>Track Your Improvements:</h3>
              <ul>
                {videoAnalysis.progressTracking.improvements.map((improvement, index) => (
                  <li key={index}>{improvement}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

                   {/* Analyzing overlay */}
             {isAnalyzing && (
               <div className="modal-overlay" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                 <div style={{ textAlign: 'center', color: 'white', padding: '2rem' }}>
                   <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
                   <h3>Analyzing your video...</h3>
                   <p style={{ opacity: 0.9 }}>Processing with our professional analysis engine</p>
                 </div>
               </div>
             )}

                   {/* Analysis Results Modal */}
             {showAnalysis && analysisResult && (
               <div className="modal-overlay" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
                 <div className="modal-content" style={{ 
                   maxWidth: '900px', 
                   maxHeight: '90vh', 
                   overflowY: 'auto',
                   backgroundColor: theme.colors.background,
                   border: `2px solid ${theme.colors.primary}`,
                   boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
                 }}>
                   <button
                     className="modal-close"
                     onClick={() => setShowAnalysis(false)}
                     style={{
                       position: 'absolute',
                       top: '15px',
                       right: '15px',
                       background: theme.colors.primary,
                       color: theme.colors.background,
                       border: 'none',
                       borderRadius: '50%',
                       width: '35px',
                       height: '35px',
                       fontSize: '20px',
                       fontWeight: 'bold',
                       cursor: 'pointer',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       zIndex: 1000
                     }}
                   >
                     ×
                   </button>
                   <div className="analysis-results">
                     <h2 style={{ color: theme.colors.primary, marginBottom: '20px' }}>
                       🎯 Video Analysis Results
                     </h2>
                     
                     <div className="analysis-header" style={{ 
                       backgroundColor: theme.colors.surface,
                       border: `1px solid ${theme.colors.border}`,
                       borderRadius: '8px',
                       padding: '20px'
                     }}>
                       <div className="video-info">
                         <p style={{ color: theme.colors.text, margin: '5px 0' }}><strong>Video ID:</strong> {analysisResult.videoId}</p>
                         <p style={{ color: theme.colors.text, margin: '5px 0' }}><strong>Sport:</strong> {analysisResult.sport.charAt(0).toUpperCase() + analysisResult.sport.slice(1)}</p>
                         <p style={{ color: theme.colors.text, margin: '5px 0' }}><strong>Analyzed:</strong> {new Date(analysisResult.timestamp).toLocaleString()}</p>
                       </div>
                       <div className="overall-score">
                         <div className="score-circle" style={{ 
                           backgroundColor: analysisResult.overallScore >= 90 ? '#4CAF50' : 
                                           analysisResult.overallScore >= 80 ? '#FF9800' : '#F44336',
                           color: 'white',
                           width: '80px',
                           height: '80px',
                           borderRadius: '50%',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           fontSize: '24px',
                           fontWeight: 'bold'
                         }}>
                           {analysisResult.overallScore}
                         </div>
                         <p style={{ textAlign: 'center', marginTop: '10px' }}>Overall Score</p>
                       </div>
                     </div>

                     <div className="analysis-sections">
                       <div className="swing-analysis" style={{ 
                         backgroundColor: theme.colors.surface,
                         border: `1px solid ${theme.colors.border}`,
                         borderRadius: '8px',
                         padding: '20px'
                       }}>
                         <h3 style={{ color: theme.colors.primary, margin: '0 0 15px 0' }}>🏌️ Swing Analysis</h3>
                         <div className="swing-metrics">
                           {Object.entries(analysisResult.analysis.swingAnalysis).map(([key, value]: [string, any]) => (
                             <div key={key} className="metric-item" style={{ 
                               backgroundColor: theme.colors.background,
                               border: `1px solid ${theme.colors.border}`,
                               borderRadius: '6px',
                               padding: '15px',
                               borderLeft: `4px solid ${theme.colors.primary}`
                             }}>
                               <div className="metric-header">
                                 <span className="metric-name" style={{ color: theme.colors.text, fontWeight: 'bold' }}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                 <span className="metric-score" style={{ 
                                   color: value.score >= 90 ? '#4CAF50' : 
                                          value.score >= 80 ? '#FF9800' : '#F44336',
                                   fontWeight: 'bold',
                                   fontSize: '18px'
                                 }}>
                                   {value.score}/100
                                 </span>
                               </div>
                               <p className="metric-feedback" style={{ color: theme.colors.textSecondary, margin: '8px 0 0 0' }}>{value.feedback}</p>
                             </div>
                           ))}
                         </div>
                       </div>

                       <div className="key-metrics" style={{ 
                         backgroundColor: theme.colors.surface,
                         border: `1px solid ${theme.colors.border}`,
                         borderRadius: '8px',
                         padding: '20px'
                       }}>
                         <h3 style={{ color: theme.colors.primary, margin: '0 0 15px 0' }}>📊 Key Metrics</h3>
                         <div className="metrics-grid">
                           {Object.entries(analysisResult.analysis.keyMetrics).map(([key, value]: [string, any]) => (
                             <div key={key} className="metric-card" style={{ 
                               backgroundColor: theme.colors.background,
                               border: `1px solid ${theme.colors.border}`,
                               borderRadius: '6px',
                               padding: '15px',
                               textAlign: 'center'
                             }}>
                               <span className="metric-label" style={{ 
                                 color: theme.colors.textSecondary,
                                 fontSize: '12px',
                                 textTransform: 'uppercase',
                                 letterSpacing: '0.5px',
                                 display: 'block',
                                 marginBottom: '5px'
                               }}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                               <span className="metric-value" style={{ 
                                 color: theme.colors.text,
                                 fontSize: '16px',
                                 fontWeight: 'bold',
                                 display: 'block'
                               }}>{value}</span>
                             </div>
                           ))}
                         </div>
                       </div>

                       <div className="recommendations" style={{ 
                         backgroundColor: theme.colors.surface,
                         border: `1px solid ${theme.colors.border}`,
                         borderRadius: '8px',
                         padding: '20px'
                       }}>
                         <h3 style={{ color: theme.colors.primary, margin: '0 0 15px 0' }}>💡 Recommendations</h3>
                         <ul style={{ margin: '0', paddingLeft: '20px' }}>
                           {analysisResult.analysis.recommendations.map((rec: string, index: number) => (
                             <li key={index} style={{ 
                               color: theme.colors.textSecondary,
                               marginBottom: '8px',
                               fontSize: '14px'
                             }}>{rec}</li>
                           ))}
                         </ul>
                       </div>

                       <div className="pose-estimation" style={{ 
                         backgroundColor: theme.colors.surface,
                         border: `1px solid ${theme.colors.border}`,
                         borderRadius: '8px',
                         padding: '20px'
                       }}>
                         <h3 style={{ color: theme.colors.primary, margin: '0 0 15px 0' }}>🤖 Pose Estimation</h3>
                         <div className="pose-metrics">
                           <div className="pose-metric" style={{ 
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             padding: '10px',
                             backgroundColor: theme.colors.background,
                             borderRadius: '4px',
                             marginBottom: '8px'
                           }}>
                             <span style={{ color: theme.colors.text }}>Key Points Detected:</span>
                             <span style={{ color: theme.colors.text, fontWeight: 'bold' }}>{analysisResult.poseEstimation.keyPoints}</span>
                           </div>
                           <div className="pose-metric" style={{ 
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             padding: '10px',
                             backgroundColor: theme.colors.background,
                             borderRadius: '4px',
                             marginBottom: '8px'
                           }}>
                             <span style={{ color: theme.colors.text }}>Confidence:</span>
                             <span style={{ color: theme.colors.text, fontWeight: 'bold' }}>{(analysisResult.poseEstimation.confidence * 100).toFixed(1)}%</span>
                           </div>
                           <div className="pose-metric" style={{ 
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             padding: '10px',
                             backgroundColor: theme.colors.background,
                             borderRadius: '4px'
                           }}>
                             <span style={{ color: theme.colors.text }}>Tracking Quality:</span>
                             <span style={{ color: theme.colors.text, fontWeight: 'bold' }}>{analysisResult.poseEstimation.trackingQuality}</span>
                           </div>
                         </div>
                       </div>
                     </div>

                     <div className="analysis-actions">
                       <button
                         className="action-button"
                         style={{
                           backgroundColor: theme.colors.primary,
                           color: theme.colors.background,
                           marginRight: '10px'
                         }}
                         onClick={() => {
                           // TODO: Save analysis results
                           alert('Analysis saved to your profile!');
                         }}
                       >
                         💾 Save Analysis
                       </button>
                       <button
                         className="action-button"
                         style={{
                           backgroundColor: theme.colors.surface,
                           color: theme.colors.text,
                           border: `1px solid ${theme.colors.border}`
                         }}
                         onClick={() => setShowAnalysis(false)}
                       >
                         Close
                       </button>
                     </div>
                   </div>
                 </div>
               </div>
             )}

             {/* Membership Upgrade Modal */}
             {showUpgrade && (
               <div className="modal-overlay">
                 <div className="modal-content">
                   <button
                     className="modal-close"
                     onClick={() => setShowUpgrade(false)}
                   >
                     ×
                   </button>
                   <MembershipUpgrade
                     currentTier={membership?.tier || 'free'}
                     sport={currentSport.id as 'pickleball' | 'tennis' | 'golf'}
                     compact
                     onSuccess={() => {
                       loadMembership();
                       setShowUpgrade(false);
                     }}
                   />
                 </div>
               </div>
             )}

             {/* Premium Prompt Modal */}
             <PremiumPrompt
               isOpen={showPremiumPrompt}
               onClose={() => setShowPremiumPrompt(false)}
               feature="Video Analysis"
               description="Get professional video analysis with AI-powered insights, pose estimation, and personalized recommendations to improve your technique."
             />
           </div>
         );
       };

export default VideoAnalysisScreen;